// src/components/3dViewer/InitialZoomController.tsx

import { useQuery } from '@tanstack/react-query';
import {
	Cesium3DTileset,
	Math as CesiumMath,
	defined,
	HeadingPitchRange,
	PrimitiveCollection,
} from 'cesium';
import React, { useEffect, useRef } from 'react';
import { useCesium } from '@/context/CesiumContext';
import { findDamagePolygon } from '@/helper/cesiumHelper';
import { getDamageViewOffset } from '@/helper/damageView';
import { assetPositionFlyOptions } from '@/lib/utils/homeView';
import { getAssetOptions } from '@/queries/assets/get-asset';
import { useQueryListAssetSensorsOptions } from '@/queries/assets/sensors/list-sensors';
import { getListDamagesByInspectionOptions } from '@/queries/damages/list-damages';
import { ROLE } from '../../common/role';
import { usePermission } from '../../hooks/use-permission';

// --- HELPER 1: Recursively find the tileset ---
const findTilesetRecursive = (collection: PrimitiveCollection): Cesium3DTileset | null => {
	const length = collection.length;
	for (let i = 0; i < length; i++) {
		const p = collection.get(i);
		if ((p as any).isLayer === true && p.show !== false) return p as Cesium3DTileset;
		if (p instanceof Cesium3DTileset && p.show !== false) return p;
		if (
			p instanceof PrimitiveCollection ||
			(typeof (p as any).get === 'function' && typeof (p as any).length === 'number')
		) {
			const found = findTilesetRecursive(p as PrimitiveCollection);
			if (found) return found;
		}
	}
	return null;
};

interface Props {
	assetId: string;
	inspectionId?: string;
}

export const InitialZoomController: React.FC<Props> = ({ assetId, inspectionId }) => {
	const { viewer, tilesLoaded, setHomeView, subSurfaceLayers } = useCesium();
	const hasZoomed = useRef(false);
	const shouldReadDamages = usePermission(ROLE.DAMAGES_READ);

	// 1. Fetch Asset Data
	const { data: assetData, isLoading: isAssetLoading } = useQuery({
		...getAssetOptions(assetId),
		refetchOnWindowFocus: false,
	});

	// 2. Fetch Sensor Data
	const { data: sensorData, isLoading: isSensorsLoading } = useQuery({
		...useQueryListAssetSensorsOptions(assetId),
		refetchOnWindowFocus: false,
	});

	// 3. Fetch Damages
	const { data: damagesData } = useQuery({
		...getListDamagesByInspectionOptions(inspectionId as string, { limit: 1, offset: 0 }, !!assetId && shouldReadDamages),
		refetchOnWindowFocus: false,
	});

	// A saved home view set or reset after the initial zoom (via the toolbar) must
	// flow into the Home button too, so run the priority chain again when it changes.
	const savedHomeViewKey = assetData?.homeView ? JSON.stringify(assetData.homeView) : '';
	const lastSavedHomeViewKey = useRef(savedHomeViewKey);
	useEffect(() => {
		if (lastSavedHomeViewKey.current === savedHomeViewKey) return;
		lastSavedHomeViewKey.current = savedHomeViewKey;
		hasZoomed.current = false;
	}, [savedHomeViewKey]);

	useEffect(() => {
		if (!viewer || hasZoomed.current) return;

		// --- PRIORITY 0/1: Saved home view ("Set current view as home"), else the asset location (FASTEST) ---
		const position = !isAssetLoading && assetData ? assetPositionFlyOptions(assetData) : null;
		if (position) {
			console.info(`[Zoom] Priority ${assetData?.homeView ? '0: Saved home view' : '1: Asset Location'}`);
			const flyOptions = { ...position, duration: 2.0 };
			setHomeView({ type: 'fly', options: flyOptions });
			viewer.camera.flyTo(flyOptions);
			hasZoomed.current = true;
			return;
		}
		if (isAssetLoading) return; // Wait for Asset Data

		// --- PRIORITY 2: Visible 3D Tiles (FAST) ---
		if (tilesLoaded) {
			const targetTileset = findTilesetRecursive(viewer.scene.primitives);
			if (targetTileset) {
				console.info('[Zoom] Priority 2: 3D Tileset found');
				const offset = new HeadingPitchRange(0, CesiumMath.toRadians(-45), 0);
				const performZoom = () => {
					viewer.zoomTo(targetTileset, offset).catch(console.warn);
					setHomeView({ type: 'zoom', target: targetTileset, offset });
					hasZoomed.current = true;
				};
				if (defined((targetTileset as any).readyPromise)) {
					(targetTileset as any).readyPromise.then(performZoom).catch(performZoom);
				} else {
					performZoom();
				}
				return;
			}
		}
		if (!tilesLoaded) return; // Wait for Tiles

		// --- PRIORITY 3: Sensors / DataSources (CHECK VISIBILITY) ---
		if (isSensorsLoading) return; // Wait for API

		const hasSensorsInData = sensorData?.sensors && sensorData.sensors.length > 0;

		if (hasSensorsInData) {
			// Check if SensorLoader has finished registering the layer
			const isLayerRegistered = subSurfaceLayers.some((l) => l.id === 'sensors-layer');

			if (!isLayerRegistered) {
				// Data exists, but Loader hasn't finished yet. WE MUST WAIT.
				return;
			}

			// Loader finished. Now check visibility.
			const sensorLayer = viewer.dataSources.getByName('sensors-layer')[0];

			if (sensorLayer && sensorLayer.show && sensorLayer.entities.values.length > 0) {
				// READY & VISIBLE -> ZOOM
				console.info('[Zoom] Priority 3: Sensors found');
				const offset = new HeadingPitchRange(0, CesiumMath.toRadians(-45), 500);
				viewer.flyTo(sensorLayer, { duration: 1.5, offset }).catch(console.warn);
				setHomeView({ type: 'zoom', target: sensorLayer, offset });
				hasZoomed.current = true;
				return;
			}

			// If we are here, it means:
			// 1. Layer is registered.
			// 2. But show === false OR entities === 0.
			// ACTION: Do nothing here, allow code to fall through to Damages/World.
		}

		// --- PRIORITY 4: Damages ---
		if (damagesData?.damages && damagesData.damages.length > 0) {
			const damage = damagesData.damages[0];
			if (damage.id) {
				const entity = findDamagePolygon(viewer, damage.id);
				if (entity) {
					console.info(`[Zoom] Priority 4: Damage (${damage.id})`);
					hasZoomed.current = true;
					// The offset waits for the tiles around the damage, so the camera lands on its visible side.
					getDamageViewOffset(viewer, entity)
						.then((offset) => {
							if (viewer.isDestroyed()) return;
							setHomeView({ type: 'zoom', target: entity, offset });
							return viewer.zoomTo(entity, offset);
						})
						.catch(console.warn);
					return;
				}
			}
		}

		// --- FALLBACK: Global View ---
		// Only reach here if:
		// 1. Asset loaded & invalid loc
		// 2. Tiles loaded & no tileset found
		// 3. Sensors loaded & no sensors found (or sensors disabled/hidden)
		// 4. Damages checked & none found
		console.warn('[Zoom] No priorities met. Zooming to Global Home.');
		viewer.camera.flyHome(2.0);
		hasZoomed.current = true;
	}, [
		viewer,
		hasZoomed,
		isAssetLoading,
		assetData,
		tilesLoaded,
		isSensorsLoading,
		sensorData,
		subSurfaceLayers, // Triggers re-run when SensorLoader finishes
		damagesData,
	]);

	return null;
};
